import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import {
  SreAgentSection,
  SRE_SETTINGS_DEFAULTS,
  isValidSreSettings,
  type SreSettingsDraft,
} from './SreAgentSection';
import type {
  PaperclipCatalog,
  ProjectRepository,
  RepositoryProvisioningEntry,
  RoutingExecutor,
  SreConfigMode,
  TriagerMode,
  TriagerWipHolder,
} from '../../services/api/botAdmin';

/**
 * Phase 129 (Plan 129-06 / D-11): the project's queue-config editor, extracted from
 * `ProjectQueueConfigForm` so Bot Admin (mode `admin`) and the project Engineering Robot card (mode
 * `engineering`) share one copy of the field logic.
 *
 * The editor is driven by a project id and a stored row, owns the field state (initialised from the
 * row at mount only, so a background refetch never overwrites an edit in progress) and calls no data
 * hook of its own beyond the SRE section's stats query (admin mode). Everything else — the data, the
 * save mutation, the admin-only extras — comes in through props, which is what lets Bot Admin keep
 * its exact hook imports.
 */

export type QueueConfigEditorMode = 'admin' | 'engineering';

/**
 * The stored config as the editor reads it. The admin-only fields (WIP holders, bucket label / key
 * and the `sre*` settings) are optional: the Engineering view never carries them (D-08), and the
 * engineering branch below never reads them even when a caller passes them.
 */
export interface QueueConfigEditorRow {
  projectId: number;
  enabled: boolean;
  sourceTaskStateIds: number[];
  pollIntervalSeconds: number;
  dailyBudget: number;
  autoMergeOnGreen: boolean;
  triagerMode: TriagerMode;
  triagerWipLimit: number;
  triagerApprovalCap: number;
  triagerApprovals: { open: number; cap: number };
  triagerWip: {
    count: number;
    limit: number;
    buckets: { count: number; full: boolean; key?: string; label?: string; shared?: boolean }[];
    holders?: TriagerWipHolder[];
  };
  paperclipCompanyId: string | null;
  paperclipProjectId: string | null;
  repositories?: ProjectRepository[];
  repositoryProvisioning?: RepositoryProvisioningEntry[];
  dailyBudgetUsedCount: number;
  dailyBudgetResetDate: string | null;
  sreEnabled?: boolean;
  sreMode?: SreConfigMode;
  sreTickBudget?: number;
  sreDailyBudget?: number;
  sreAuditBudget?: number;
}

/**
 * What the editor hands to its `onSave`. Engineering mode fills only the first block (T-129-21);
 * admin mode adds the optional rest, exactly as Bot Admin sent it before the extraction.
 */
export interface QueueConfigEditorSaveInput {
  enabled: boolean;
  sourceTaskStateIds: number[];
  pollIntervalSeconds: number;
  dailyBudget: number;
  triagerMode: TriagerMode;
  triagerWipLimit: number;
  triagerApprovalCap: number;
  paperclipCompanyId: string;
  paperclipProjectId?: string;
  repositories?: ProjectRepository[];
  autoMergeOnGreen?: boolean;
  sreEnabled?: boolean;
  sreMode?: SreConfigMode;
  sreTickBudget?: number;
  sreDailyBudget?: number;
  sreAuditBudget?: number;
  fallbackExecutor?: RoutingExecutor;
}

/** Phase 118 (Plan 118-02 / PCTRIAGE-08): WIP-limit bounds, mirrored from the PUT schema (1..5). */
const TRIAGER_WIP_LIMIT_MIN = 1;
const TRIAGER_WIP_LIMIT_MAX = 5;

/** Today's date in Europe/Prague as YYYY-MM-DD, the day boundary the daily budget resets on. */
function pragueToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague' }).format(new Date());
}

/** Picks consumed today: the stored count only counts when its reset date is today. */
function picksUsedToday(row: QueueConfigEditorRow): number {
  return row.dailyBudgetResetDate === pragueToday() ? row.dailyBudgetUsedCount : 0;
}

/**
 * Phase 125 (D-17): the per-bucket lines (and the per-person note on the limit) show only when
 * a person's own agent has a bucket; with person agents off there is one shared bucket and the
 * plain total line is all there is to say. Admin mode only: it reads the bucket key.
 */
export function hasPerPersonBuckets(buckets: QueueConfigEditorRow['triagerWip']['buckets']): boolean {
  return buckets.length > 1 || buckets.some((b) => b.key?.startsWith('person:') ?? false);
}

function isValidWipLimit(value: number): boolean {
  return Number.isInteger(value) && value >= TRIAGER_WIP_LIMIT_MIN && value <= TRIAGER_WIP_LIMIT_MAX;
}

/**
 * Phase 123 (MKS-199 / PCAPPROVE-06): open-approval cap, mirrored from the PUT schema. Any positive
 * integer since the upper bound of 10 was dropped (migration 20261008_003).
 */
const TRIAGER_APPROVAL_CAP_MIN = 1;

/** Factory™ is the only executor left (Monka no longer builds), so every admin save sends it. */
const FALLBACK_EXECUTOR: RoutingExecutor = 'paperclip';
const TRIAGER_APPROVAL_CAP_DEFAULT = '3';

function isValidApprovalCap(value: number): boolean {
  return Number.isInteger(value) && value >= TRIAGER_APPROVAL_CAP_MIN;
}

/**
 * MKS-312 (Plan 126-05 / D-19, D-20): the repository editor's row. `key` is a client-only React key;
 * it is never sent. Mirrors the PUT schema's repository item (server validation is authoritative —
 * the hints below are a convenience).
 */
interface RepositoryDraft {
  key: number;
  repository: string;
  paperclipProjectId: string;
  description: string;
  isMain: boolean;
}

/** Same pattern as the PUT schema's repository item. */
const REPOSITORY_SLUG_PATTERN = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;

let repositoryDraftKey = 0;
function newRepositoryDraft(overrides: Partial<RepositoryDraft> = {}): RepositoryDraft {
  repositoryDraftKey += 1;
  return {
    key: repositoryDraftKey,
    repository: '',
    paperclipProjectId: '',
    description: '',
    isMain: false,
    ...overrides,
  };
}

function toRepositoryDrafts(list: ProjectRepository[] | undefined): RepositoryDraft[] {
  return (list ?? []).map((r) =>
    newRepositoryDraft({
      repository: r.repository,
      paperclipProjectId: r.paperclipProjectId,
      description: r.description ?? '',
      isMain: r.isMain,
    })
  );
}

/** Client-side hints for the repository list; an empty list is always valid (it clears the list). */
function repositoryListHints(rows: RepositoryDraft[]): string[] {
  if (rows.length === 0) return [];
  const hints: string[] = [];
  if (rows.some((r) => !REPOSITORY_SLUG_PATTERN.test(r.repository.trim()))) {
    hints.push('Every repository must be written as owner/name.');
  }
  if (rows.some((r) => r.paperclipProjectId.trim() === '')) {
    hints.push('Every repository needs a Factory™ project.');
  }
  if (rows.filter((r) => r.isMain).length !== 1) {
    hints.push('Mark exactly one repository as the main repository.');
  }
  return hints;
}

/** MKS-471 (Plan 128-07 / D-15): the chip text of one linked repository's provisioning state. */
function provisioningChipText(entry: RepositoryProvisioningEntry): string {
  switch (entry.status) {
    case 'pending':
      return 'Pending: creating the Factory™ project';
    case 'created':
      return 'Created';
    case 'failed':
      return `Failed: ${entry.errorCode ?? 'unknown'}`;
    default:
      return 'Not requested';
  }
}

const PROVISIONING_CHIP_CLASS: Record<RepositoryProvisioningEntry['status'], string> = {
  pending: 'bg-amber-50 text-amber-700',
  created: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  none: 'bg-gray-100 text-gray-600',
};

/**
 * MKS-471 (Plan 128-07 / D-15, D-21): one line per repository linked to the project, with the state
 * of its Factory™ project provisioning and a Retry for a failed or never-requested one. Pure: the
 * caller owns the retry mutation (Bot Admin and the Engineering card each have their own), and
 * renders this only for a project that has at least one provisioning entry, so a retry mutation is
 * only ever mounted with something to retry.
 */
export function RepositoryProvisioningList({
  entries,
  onRetry,
  retryingRepository,
  retryFailed = false,
}: {
  entries: RepositoryProvisioningEntry[];
  onRetry: (repository: string) => void;
  /** The repository whose retry is in flight, or null. Every Retry is disabled while one is. */
  retryingRepository: string | null;
  retryFailed?: boolean;
}) {
  const retrying = retryingRepository !== null;
  return (
    <div className="mt-3" data-testid="queue-config-repository-provisioning">
      <p className="text-sm font-medium text-gray-700">Linked repository provisioning</p>
      <ul className="mt-1 space-y-1">
        {entries.map((entry) => (
          <li
            key={entry.repository}
            className="flex flex-wrap items-center gap-2 text-sm text-gray-700"
            data-testid="queue-config-provisioning-entry"
          >
            <span className="font-mono text-xs">{entry.repository}</span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${PROVISIONING_CHIP_CLASS[entry.status]}`}
            >
              {provisioningChipText(entry)}
            </span>
            {(entry.status === 'failed' || entry.status === 'none') && (
              <button
                type="button"
                onClick={() => onRetry(entry.repository)}
                disabled={retrying}
                aria-label={`Retry ${entry.repository}`}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
              >
                Retry
              </button>
            )}
          </li>
        ))}
      </ul>
      {retryFailed && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          The retry could not be requested. Try again in a moment.
        </p>
      )}
    </div>
  );
}

export interface ProjectQueueConfigEditorProps {
  mode: QueueConfigEditorMode;
  projectId: number;
  /** The stored config; undefined or null for a project that has none yet (today's defaults). */
  row: QueueConfigEditorRow | undefined | null;
  projectStates: { id: number; name: string }[] | undefined;
  catalog: PaperclipCatalog | undefined;
  onSave: (input: QueueConfigEditorSaveInput, options: { onSuccess: () => void }) => void;
  savePending: boolean;
  saveError?: string | null;
  /** Called only when the row has provisioning entries. */
  renderProvisioning?: (entries: RepositoryProvisioningEntry[]) => ReactNode;
  /** Admin only: the inline GitHub link block, rendered above the fields. */
  githubLinkSlot?: ReactNode;
  /** Admin only: the catalog Refresh button and its messages. Gets the company being edited. */
  catalogRefreshSlot?: (context: { paperclipCompanyId: string }) => ReactNode;
  /** A number the wrapper bumps after it disabled this project; unchecks Enabled. */
  disabledSignal?: number;
}

/**
 * State initialises from `row` at mount only. The caller keys the editor by project (Bot Admin) or
 * mounts it once per project (Engineering card), so a refetch that changes `row` never re-seeds the
 * fields — which is what keeps the repository rows safe (Pitfall 7, T-129-22).
 */
export function ProjectQueueConfigEditor({
  mode,
  projectId,
  row,
  projectStates,
  catalog,
  onSave,
  savePending,
  saveError,
  renderProvisioning,
  githubLinkSlot,
  catalogRefreshSlot,
  disabledSignal,
}: ProjectQueueConfigEditorProps) {
  const isAdmin = mode === 'admin';
  const [enabled, setEnabled] = useState(row?.enabled ?? false);
  const [sourceTaskStateIds, setSourceTaskStateIds] = useState<number[]>(
    row?.sourceTaskStateIds ?? []
  );
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState(
    String(row?.pollIntervalSeconds ?? 300)
  );
  const [dailyBudget, setDailyBudget] = useState(String(row?.dailyBudget ?? 10));
  // Phase 118 (Plan 118-02 / PCTRIAGE-08, D-118-13): the triager's mode and WIP cap. Greedy mode
  // (Phase 83) was retired by D-118-19 and is gone from here.
  const [triagerMode, setTriagerMode] = useState<TriagerMode>(row?.triagerMode ?? 'off');
  const [triagerWipLimit, setTriagerWipLimit] = useState(String(row?.triagerWipLimit ?? 1));
  // Phase 123 (MKS-199): the cap on new-feature picks waiting for PM approval.
  const [triagerApprovalCap, setTriagerApprovalCap] = useState(
    row ? String(row.triagerApprovalCap) : TRIAGER_APPROVAL_CAP_DEFAULT
  );
  // Phase 113 (Plan 113-04 / PCSYNC-01): the Factory™ company/project this project's
  // routed-to-paperclip work dispatches into. null (unconfigured) shows as an empty string;
  // submitting untouched round-trips it as '', which the server normalizes to NULL again.
  const [paperclipCompanyId, setPaperclipCompanyId] = useState(row?.paperclipCompanyId ?? '');
  const [paperclipProjectId, setPaperclipProjectId] = useState(row?.paperclipProjectId ?? '');
  // MKS-312 (Plan 126-05 / D-19): the repository list editor.
  const [repoRows, setRepoRows] = useState<RepositoryDraft[]>(() =>
    toRepositoryDrafts(row?.repositories)
  );
  // MKS-471 (Plan 128-07, Pitfall 7): the repository rows or the project mapping were edited here.
  // An unedited mapping is not submitted, so a form loaded before an automatic provisioning added a
  // row can never delete it or null the binding. Reset after a successful save.
  const [repositoriesDirty, setRepositoriesDirty] = useState(false);
  // Phase 127 (Plan 127-07, D-24, D-25): the SRE agent's switch, mode and budgets (admin only).
  const [sreDraft, setSreDraft] = useState<SreSettingsDraft>(() =>
    row
      ? {
          enabled: row.sreEnabled ?? SRE_SETTINGS_DEFAULTS.enabled,
          mode: row.sreMode ?? SRE_SETTINGS_DEFAULTS.mode,
          tickBudget: String(row.sreTickBudget ?? SRE_SETTINGS_DEFAULTS.tickBudget),
          dailyBudget: String(row.sreDailyBudget ?? SRE_SETTINGS_DEFAULTS.dailyBudget),
          auditBudget: String(row.sreAuditBudget ?? SRE_SETTINGS_DEFAULTS.auditBudget),
        }
      : SRE_SETTINGS_DEFAULTS
  );

  // The wrapper disabled this project: show Enabled off, as a successful disable leaves it.
  const seenDisabledSignal = useRef(disabledSignal);
  useEffect(() => {
    if (disabledSignal !== seenDisabledSignal.current) {
      seenDisabledSignal.current = disabledSignal;
      setEnabled(false);
    }
  }, [disabledSignal]);

  // Phase 115 (Plan 115-04 / PCPROV-01): an empty or never-fetched catalog degrades both fields to
  // free-text inputs (D-115-03's "never lock the admin out" contract).
  const catalogCompanies = catalog?.companies ?? [];
  const catalogProjects = catalog?.projects ?? [];
  const hasCatalog = catalogCompanies.length > 0;
  const catalogProjectsForSelectedCompany = catalogProjects.filter(
    (p) => p.companyId === paperclipCompanyId
  );

  const toggleSourceState = (stateId: number) => {
    setSourceTaskStateIds((prev) =>
      prev.includes(stateId) ? prev.filter((id) => id !== stateId) : [...prev, stateId]
    );
  };

  // Phase 115 (Plan 115-04 / PCPROV-01): selecting a company clears a previously selected project
  // that does not belong to it, so the picker can never submit a company/project pair from two
  // different companies.
  const handlePaperclipCompanyChange = (value: string) => {
    setRepositoriesDirty(true);
    setPaperclipCompanyId(value);
    const stillValid = catalogProjects.some(
      (p) => p.companyId === value && p.id === paperclipProjectId
    );
    if (!stillValid) {
      setPaperclipProjectId('');
    }
    // MKS-312: a repository row can never keep a project of the company just left behind.
    // Without a catalog the projects are free text and nothing can be judged, so rows stay.
    if (hasCatalog) {
      setRepoRows((prev) =>
        prev.map((r) =>
          catalogProjects.some((p) => p.companyId === value && p.id === r.paperclipProjectId)
            ? r
            : { ...r, paperclipProjectId: '' }
        )
      );
    }
  };

  const handlePaperclipProjectChange = (value: string) => {
    setRepositoriesDirty(true);
    setPaperclipProjectId(value);
  };

  const updateRepoRow = (key: number, patch: Partial<RepositoryDraft>) => {
    setRepositoriesDirty(true);
    setRepoRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const setMainRepoRow = (key: number) => {
    setRepositoriesDirty(true);
    setRepoRows((prev) => prev.map((r) => ({ ...r, isMain: r.key === key })));
  };

  const addRepoRow = () => {
    setRepositoriesDirty(true);
    // The first row is the main one by default, so a one-row list is valid straight away.
    setRepoRows((prev) => [...prev, newRepositoryDraft({ isMain: prev.length === 0 })]);
  };

  const removeRepoRow = (key: number) => {
    setRepositoriesDirty(true);
    setRepoRows((prev) => prev.filter((r) => r.key !== key));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const poll = Number(pollIntervalSeconds);
    const budget = Number(dailyBudget);
    const wipLimit = Number(triagerWipLimit);
    const approvalCap = Number(triagerApprovalCap);
    if (!Number.isFinite(poll) || !Number.isFinite(budget)) return;
    if (!isValidWipLimit(wipLimit)) return;
    if (!isValidApprovalCap(approvalCap)) return;
    if (isAdmin && !isValidSreSettings(sreDraft)) return;
    // MKS-312: the repository list is submitted, and the main row's project IS the scalar mapping.
    if (repositoryListHints(repoRows).length > 0) return;
    // The `repositoriesDirty` rule: the repository rows and the project mapping are submitted only
    // after they were edited here (Pitfall 7); the server treats omission as unchanged.
    const sendMapping = repositoriesDirty;
    const mappingBody: { paperclipProjectId?: string; repositories?: ProjectRepository[] } = {};
    if (sendMapping) {
      // The main row's project IS the scalar mapping.
      let submittedProjectId = paperclipProjectId;
      const main = repoRows.find((r) => r.isMain);
      if (main) submittedProjectId = main.paperclipProjectId.trim();
      mappingBody.paperclipProjectId = submittedProjectId;
      mappingBody.repositories = repoRows.map((r) => ({
        repository: r.repository.trim(),
        paperclipProjectId: r.paperclipProjectId.trim(),
        description: r.description.trim() === '' ? null : r.description.trim(),
        isMain: r.isMain,
      }));
    }
    const memberKeys = {
      enabled,
      sourceTaskStateIds,
      pollIntervalSeconds: poll,
      dailyBudget: budget,
      triagerMode,
      triagerWipLimit: wipLimit,
      triagerApprovalCap: approvalCap,
      paperclipCompanyId,
      ...mappingBody,
    };
    const input: QueueConfigEditorSaveInput = isAdmin
      ? {
          ...memberKeys,
          // No longer editable here (it only ever drove Monka); keep what is stored.
          autoMergeOnGreen: row?.autoMergeOnGreen ?? false,
          sreEnabled: sreDraft.enabled,
          sreMode: sreDraft.mode,
          sreTickBudget: Number(sreDraft.tickBudget),
          sreDailyBudget: Number(sreDraft.dailyBudget),
          sreAuditBudget: Number(sreDraft.auditBudget),
          // Factory™ is the only executor; every save pins the project to it.
          fallbackExecutor: FALLBACK_EXECUTOR,
        }
      : // T-129-21: the engineering branch sends the member keys and nothing else.
        memberKeys;
    // The saved state is now the baseline again: the next unrelated save omits the mapping.
    onSave(input, { onSuccess: () => setRepositoriesDirty(false) });
  };

  const provisioningEntries = row?.repositoryProvisioning ?? [];

  return (
    <>
      <form onSubmit={handleSave} className="space-y-3">
        {githubLinkSlot}

        <div className="flex items-center gap-2">
          <input
            id="queue-config-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="queue-config-enabled" className="text-sm font-medium text-gray-700">
            Enabled
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="w-40">
            <Input
              id="queue-config-poll-interval"
              label="Cadence (seconds)"
              type="number"
              min={30}
              value={pollIntervalSeconds}
              onChange={(e) => setPollIntervalSeconds(e.target.value)}
            />
          </div>
          <div className="w-56">
            <label
              htmlFor="queue-config-paperclip-company"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Factory™ company
            </label>
            {hasCatalog ? (
              <select
                id="queue-config-paperclip-company"
                value={paperclipCompanyId}
                onChange={(e) => handlePaperclipCompanyChange(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Unset</option>
                {catalogCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.id})
                  </option>
                ))}
              </select>
            ) : (
              <>
                <Input
                  id="queue-config-paperclip-company"
                  type="text"
                  value={paperclipCompanyId}
                  onChange={(e) => setPaperclipCompanyId(e.target.value)}
                  placeholder="Unset"
                />
                <p className="mt-1 text-xs text-gray-400">
                  No catalog available yet — enter the Factory™ company ID by hand (from
                  Factory™&apos;s own UI). Empty means a routed task here reports a dispatch failure
                  naming the missing mapping.
                </p>
              </>
            )}
          </div>
          {/* MKS-312 — with a non-empty repository list, the main row's project replaces this
           * single picker; an empty list keeps it so the mapping is never lost. */}
          {repoRows.length === 0 && (
            <div className="w-56">
              <label
                htmlFor="queue-config-paperclip-project"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Factory™ project
              </label>
              {hasCatalog ? (
                <select
                  id="queue-config-paperclip-project"
                  value={paperclipProjectId}
                  onChange={(e) => handlePaperclipProjectChange(e.target.value)}
                  disabled={!paperclipCompanyId}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">Unset</option>
                  {catalogProjectsForSelectedCompany.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.id})
                    </option>
                  ))}
                </select>
              ) : (
                <>
                  <Input
                    id="queue-config-paperclip-project"
                    type="text"
                    value={paperclipProjectId}
                    onChange={(e) => handlePaperclipProjectChange(e.target.value)}
                    placeholder="Unset"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    No catalog available yet — enter the Factory™ project ID by hand (from
                    Factory™&apos;s own UI). Empty means a routed task here reports a dispatch
                    failure naming the missing mapping.
                  </p>
                </>
              )}
            </div>
          )}
          <div className="w-full basis-full" data-testid="queue-config-repositories">
            <p className="text-sm font-medium text-gray-700">Repositories</p>
            <p className="mt-0.5 text-xs text-gray-400">
              The repositories a Factory™ ticket of this project may touch. The main
              repository&apos;s Factory™ project is the project&apos;s binding. Leave empty for a
              single-repository project.
            </p>
            {!paperclipCompanyId && repoRows.length > 0 && (
              <p className="mt-1 text-xs text-amber-600">
                Choose the Factory™ company above before saving a repository list.
              </p>
            )}
            <ul className="mt-2 space-y-2">
              {repoRows.map((r, index) => (
                <li
                  key={r.key}
                  className="flex flex-wrap items-end gap-2"
                  data-testid="queue-config-repository-row"
                >
                  <div className="w-56">
                    <Input
                      id={`queue-config-repository-${r.key}`}
                      label="Repository"
                      type="text"
                      value={r.repository}
                      onChange={(e) => updateRepoRow(r.key, { repository: e.target.value })}
                      placeholder="owner/name"
                      maxLength={140}
                    />
                  </div>
                  <div className="w-56">
                    <label
                      htmlFor={`queue-config-repository-project-${r.key}`}
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Factory™ project
                    </label>
                    {hasCatalog ? (
                      <select
                        id={`queue-config-repository-project-${r.key}`}
                        value={r.paperclipProjectId}
                        onChange={(e) =>
                          updateRepoRow(r.key, { paperclipProjectId: e.target.value })
                        }
                        disabled={!paperclipCompanyId}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                      >
                        <option value="">Unset</option>
                        {catalogProjectsForSelectedCompany.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.id})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        id={`queue-config-repository-project-${r.key}`}
                        type="text"
                        value={r.paperclipProjectId}
                        onChange={(e) =>
                          updateRepoRow(r.key, { paperclipProjectId: e.target.value })
                        }
                        placeholder="Factory™ project ID"
                      />
                    )}
                  </div>
                  <div className="w-64">
                    <Input
                      id={`queue-config-repository-description-${r.key}`}
                      label="Description"
                      type="text"
                      value={r.description}
                      onChange={(e) => updateRepoRow(r.key, { description: e.target.value })}
                      placeholder="What lives here"
                      maxLength={500}
                    />
                  </div>
                  <label className="flex items-center gap-1 pb-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="queue-config-main-repository"
                      checked={r.isMain}
                      onChange={() => setMainRepoRow(r.key)}
                      aria-label={`Main repository (row ${index + 1})`}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Main
                  </label>
                  <button
                    type="button"
                    onClick={() => removeRepoRow(r.key)}
                    aria-label={`Remove repository (row ${index + 1})`}
                    className="pb-2 text-sm font-medium text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            {repositoryListHints(repoRows).length > 0 && (
              <ul
                className="mt-1 list-disc pl-5 text-xs text-amber-600"
                data-testid="queue-config-repositories-hints"
              >
                {repositoryListHints(repoRows).map((hint) => (
                  <li key={hint}>{hint}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={addRepoRow}
              disabled={repoRows.length >= 10}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40"
            >
              Add repository
            </button>
            {provisioningEntries.length > 0 && renderProvisioning?.(provisioningEntries)}
          </div>
          <div className="w-full basis-full">
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {catalog?.fetchedAt ? (
                <span>
                  Catalog last synced{' '}
                  {formatDistanceToNow(new Date(catalog.fetchedAt), { addSuffix: true })} — this is
                  a last-known list, not a live read.
                  {catalog.stale && <span className="ml-1 font-medium text-amber-600">Stale.</span>}
                  {catalog.truncated && (
                    <span className="ml-1 font-medium text-amber-600">
                      Truncated — not every Factory™ company/project is shown.
                    </span>
                  )}
                </span>
              ) : (
                <span>Catalog never fetched yet — showing the manual entry fields above.</span>
              )}
              {catalogRefreshSlot?.({ paperclipCompanyId })}
            </div>
          </div>
        </div>

        <div
          className="space-y-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-3"
          data-testid="queue-config-triager-group"
        >
          <p className="text-sm font-semibold text-gray-900">Triager</p>
          <div className="flex flex-wrap gap-3">
            <div className="w-40">
              <label
                htmlFor="queue-config-triager-mode"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Triager mode
              </label>
              <select
                id="queue-config-triager-mode"
                value={triagerMode}
                onChange={(e) => setTriagerMode(e.target.value as TriagerMode)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="off">Off</option>
                <option value="propose">Propose</option>
                <option value="auto">Auto</option>
              </select>
            </div>
            <div className="w-40">
              <Input
                id="queue-config-triager-wip-limit"
                label="WIP limit"
                type="number"
                min={TRIAGER_WIP_LIMIT_MIN}
                max={TRIAGER_WIP_LIMIT_MAX}
                value={triagerWipLimit}
                onChange={(e) => setTriagerWipLimit(e.target.value)}
              />
            </div>
            <div className="w-56">
              <Input
                id="queue-config-triager-approval-cap"
                label="Max open approvals"
                type="number"
                min={TRIAGER_APPROVAL_CAP_MIN}
                value={triagerApprovalCap}
                onChange={(e) => setTriagerApprovalCap(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500" data-testid="queue-config-approvals-open">
                New-feature picks waiting for PM approval
                {row ? ` (${row.triagerApprovals.open} of ${row.triagerApprovalCap} open)` : ''}
              </p>
            </div>
            <div className="w-48">
              <Input
                id="queue-config-daily-budget"
                label="Max picks per day"
                type="number"
                min={0}
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
              />
              {row && (
                <p className="mt-1 text-xs text-gray-500" data-testid="queue-config-picks-used">
                  used {picksUsedToday(row)} of {row.dailyBudget} today
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="mb-1 text-sm font-medium text-gray-700">
              Candidate columns (source states)
            </p>
            {!projectStates || projectStates.length === 0 ? (
              <p className="text-sm text-gray-400">No task states on this project.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {projectStates.map((state) => (
                  <label key={state.id} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sourceTaskStateIds.includes(state.id)}
                      onChange={() => toggleSourceState(state.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {state.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          {isAdmin ? (
            <div data-testid="queue-config-triager-wip">
              <p className="mb-1 text-sm font-medium text-gray-700">
                Current WIP
                {row ? ` (${row.triagerWip.count} of ${row.triagerWip.limit})` : ''}
              </p>
              {row && hasPerPersonBuckets(row.triagerWip.buckets) && (
                <div data-testid="queue-config-triager-wip-buckets" className="mb-1">
                  <p className="text-xs text-gray-500">
                    The limit applies to each person&apos;s agent separately; everything else
                    shares one bucket.
                  </p>
                  <ul className="space-y-0.5 text-sm text-gray-700">
                    {row.triagerWip.buckets.map((b) => (
                      <li key={b.key}>
                        {b.label}: {b.count} of {row.triagerWip.limit}
                        {b.full && (
                          <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">
                            full
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!row || (row.triagerWip.holders ?? []).length === 0 ? (
                <p className="text-sm text-gray-400">Nothing holds a triager slot.</p>
              ) : (
                <ul className="space-y-0.5 text-sm text-gray-700">
                  {(row.triagerWip.holders ?? []).map((h) => (
                    <li key={`${h.kind}-${h.taskId}`}>
                      <span className="font-medium">{h.code}</span> {h.name}{' '}
                      <span className="text-xs text-gray-500">
                        ({h.kind === 'robot' ? 'robot' : 'proposal'}
                        {h.paperclipRunStatus ? `, run ${h.paperclipRunStatus}` : ''})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            // D-08: the redacted reading. Counts only; this branch never touches the holders or a
            // bucket's key or label, even when the row carries them.
            <div data-testid="queue-config-triager-wip">
              <p className="mb-1 text-sm font-medium text-gray-700">
                Current WIP
                {row ? ` (${row.triagerWip.count} of ${row.triagerWip.limit})` : ''}
              </p>
              {row && row.triagerWip.buckets.length > 1 && (
                <p
                  className="text-sm text-gray-700"
                  data-testid="queue-config-triager-wip-buckets-summary"
                >
                  {row.triagerWip.buckets.filter((b) => b.full).length} of{' '}
                  {row.triagerWip.buckets.length} buckets full
                </p>
              )}
            </div>
          )}
        </div>

        {isAdmin && (
          <SreAgentSection
            projectId={projectId}
            storedMode={row?.sreMode ?? 'triage'}
            draft={sreDraft}
            onChange={(patch) => setSreDraft((prev) => ({ ...prev, ...patch }))}
          />
        )}

        <Button type="submit" isLoading={savePending}>
          Save
        </Button>
      </form>

      {saveError && <p className="mt-2 text-sm text-red-600">{saveError}</p>}
    </>
  );
}
